import React, { useRef, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { FeatureGuardedDisaStigMenuItem } from './disa-stig-menu-item'
import {
  FeatureGuardedSbomReportMenuItem,
  SbomModal
} from './sbom-report/sbom-report-menu-item'
import { Popover, Tooltip } from '@contrast-security-inc/design-system-popovers'
import {
  IconReports,
  theme
} from '@contrast-security-inc/design-system-foundations'
import { IconButton } from '@contrast-security-inc/design-system-buttons'
import { css, cx } from '@emotion/css'
import { useOrganizationFeature } from '../organization-feature'
import { AttestationReportDialog } from '../pdf-reports/attestation-report-dialog'
import { StigModal } from './disa-stig-modal'
import { SbomType } from './sbom-report/sbom-enums'

export const testID = {
  vulnTrendsMenuItem: 'vulnTrendsMenuItem',
  reportsButton: 'reportsButton',
  menuOption: 'menu-option-button'
}

interface ApplicationReportsButtonProps {
  isFreemium: boolean
  organizationID: string
  applicationID: string
  onVulnTrendClick: () => void
  isAppUnlicensed: boolean
}

interface ApplicationReportsMenuProps {
  organizationID: string
  onVulnTrendClick: () => void
  closeMenu: () => void
  openAttestationDialog: () => void
  openStigModal: () => void
  openSbomModal: () => void
}

export function ApplicationReportsButton({
  isFreemium,
  organizationID,
  applicationID,
  onVulnTrendClick,
  isAppUnlicensed
}: ApplicationReportsButtonProps) {
  const { formatMessage } = useIntl()
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const referenceElement = useRef<HTMLButtonElement | null>(null)
  const [isAttestationDialogOpen, setIsAttestationDialogOpen] = useState(false)
  const [isStigModalOpen, setIsStigModalOpen] = useState(false)
  const [isSbomModalOpen, setIsSbomModalOpen] = useState(false)
  const { isLoading } = useOrganizationFeature(
    organizationID,
    'oss-inventory-mode'
  )

  const closeMenu = () => {
    setIsMenuOpen(false)
  }

  const openAttestationDialog = () => {
    setIsAttestationDialogOpen(true)
  }

  const openStigModal = () => {
    setIsStigModalOpen(true)
  }

  const openSbomModal = () => {
    setIsSbomModalOpen(true)
  }

  return (
    <>
      {!isFreemium && (
        <>
          <Tooltip
            placement="top"
            className={css`
              z-index: 1003;
              padding: 0px 7px;
              line-height: 1.4px;
              font-size: 12px;
            `}
            body={<FormattedMessage id="title_reports" />}
          >
            <span>
              <IconButton
                Icon={IconReports}
                aria-label={formatMessage({ id: 'title_reports' })}
                isDisabled={isAppUnlicensed}
                ref={referenceElement}
                onClick={() => setIsMenuOpen(!isMenuOpen && !isFreemium)}
                data-testid={testID.reportsButton}
              />
              {!isLoading && (
                <Popover
                  referenceElement={referenceElement?.current}
                  isOpen={isMenuOpen}
                  hasArrow
                  placement="bottom-end"
                  offset={[0, 8]}
                  onClose={setIsMenuOpen}
                >
                  <ul
                    style={{
                      listStyleType: 'none',
                      padding: '5px 0px',
                      margin: '0px'
                    }}
                  >
                    <ApplicationReportsMenu
                      organizationID={organizationID}
                      onVulnTrendClick={onVulnTrendClick}
                      closeMenu={closeMenu}
                      openAttestationDialog={openAttestationDialog}
                      openStigModal={openStigModal}
                      openSbomModal={openSbomModal}
                    />
                  </ul>
                </Popover>
              )}
            </span>
          </Tooltip>
          {isAttestationDialogOpen && (
            <AttestationReportDialog
              onCloseDialog={() => setIsAttestationDialogOpen(false)}
            />
          )}
          {isStigModalOpen && (
            <StigModal
              isOpen={isStigModalOpen}
              closeModal={() => setIsStigModalOpen(false)}
              organizationID={organizationID}
              applicationID={applicationID}
            />
          )}
          {isSbomModalOpen && (
            <SbomModal
              isOpen={isSbomModalOpen}
              closeModal={() => setIsSbomModalOpen(false)}
              organizationID={organizationID}
              applicationID={applicationID}
              sbomType={SbomType.Application}
            />
          )}
        </>
      )}
    </>
  )
}

export const reportsMenuItemStyle = cx(css`
  padding: 3px 14px;
  &:hover {
    background-color: ${theme.color.teal10};
  }
`)

export function ApplicationReportsMenu({
  organizationID,
  onVulnTrendClick,
  closeMenu,
  openAttestationDialog,
  openStigModal,
  openSbomModal
}: ApplicationReportsMenuProps) {
  const { formatMessage } = useIntl()

  const vulnTrends = (
    <li
      onClick={() => {
        closeMenu()
        onVulnTrendClick()
      }}
      data-testid={testID.vulnTrendsMenuItem}
      className={reportsMenuItemStyle}
    >
      <FormattedMessage id="action_applicationDashboard_viewVulnTrends">
        {(title) => {
          return <span>{title}</span>
        }}
      </FormattedMessage>
    </li>
  )

  const attestationReportMenuItem = (
    <li
      onClick={() => {
        closeMenu()
        openAttestationDialog()
      }}
      data-testid={testID.menuOption}
      className={reportsMenuItemStyle}
    >
      <span>
        {formatMessage({
          id: 'title_applicationDashboard_generateAttestationReport'
        })}
      </span>
    </li>
  )

  return (
    <>
      {vulnTrends}
      {attestationReportMenuItem}
      <FeatureGuardedDisaStigMenuItem
        organizationID={organizationID}
        onClick={() => {
          closeMenu()
          openStigModal()
        }}
      />
      <FeatureGuardedSbomReportMenuItem
        organizationID={organizationID}
        onClick={() => {
          closeMenu()
          openSbomModal()
        }}
      />
    </>
  )
}
